/**
 * @file src/core/track.js
 * Registry dispose tự động — cơ chế chống memory leak DUY NHẤT mà dev cần biết.
 * Vì sao cần: three.js KHÔNG tự gom Geometry/Material/Texture/RenderTarget.
 * Không dispose thì đổi tiết vài lần là leak VRAM, biểu hiện "học lâu máy chậm dần".
 */

/**
 * @typedef {{ dispose: () => void }} Disposable
 */

/**
 * @typedef {Object} Tracker
 * @property {<T>(obj: T) => T} track          Ghi danh disposable, TRẢ LẠI chính nó (dùng inline được)
 * @property {(fn: () => void) => void} onDispose  Ghi danh hàm cleanup bất kỳ
 * @property {() => void} disposeAll            Engine gọi. Dev KHÔNG gọi
 * @property {() => number} size                Số entry còn lại — smoke test kiểm phải = 0
 */

/**
 * @param {{ dev?: boolean }} [opts]
 * @returns {Tracker}
 */
export function createTracker(opts = {}) {
  const dev = opts.dev ?? false;
  /** @type {Set<Disposable>} */
  const disposables = new Set();
  /** @type {Array<() => void>} */
  const teardowns = [];

  return {
    track(obj) {
      const any = /** @type {any} */ (obj);
      if (any && typeof any.dispose === 'function') {
        disposables.add(any);
      } else if (dev) {
        console.warn('[track] đối tượng không có dispose():', obj);
      }
      return obj;
    },

    onDispose(fn) {
      if (typeof fn === 'function') teardowns.push(fn);
    },

    disposeAll() {
      // teardown theo thứ tự NGƯỢC: cái đăng ký sau cùng gỡ trước
      for (let i = teardowns.length - 1; i >= 0; i--) {
        try {
          teardowns[i]?.();
        } catch (e) {
          console.error('[track] teardown lỗi:', e);
        }
      }
      teardowns.length = 0;

      for (const d of disposables) {
        try {
          d.dispose();
        } catch (e) {
          console.error('[track] dispose lỗi:', e);
        }
      }
      disposables.clear();
    },

    size() {
      return disposables.size + teardowns.length;
    },
  };
}
